import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AUFGABEN_STATUS,
  STATUS_NAMEN,
  heuteIso,
  istUeberfaellig,
  tageZwischen,
  type Aufgabe,
  type MeineAufgabe,
} from '@beholder/shared/module/aufgaben';
import { api } from '../../core/api';
import type { ModulSeiteProps } from '../../core/modul';
import { Symbol } from '../../core/symbole';
import { Auswahl, Eingabe, Knopf, Laden, Leer, Meldung, useAktion } from '../../core/ui';
import { pfad, useAufgaben, useMeineAufgaben, useMindmap, useMitglieder } from './daten';
import { Detail } from './Detail';
import { AufgabenZeile, VorschlagKarte } from './Zeilen';
import './stil.css';

type Ansicht = 'alle' | 'meine' | 'vorschlaege' | 'ueberall';
type Faelligkeit = 'alle' | 'ueberfaellig' | 'heute' | 'woche' | 'ohne';

export interface Filter {
  ansicht: Ansicht;
  person: string; // '' = alle, '-' = niemand, sonst Benutzer-ID
  status: string; // 'aktiv' = nicht erledigt, 'alle', sonst Status
  faellig: Faelligkeit;
  suche: string;
}

const START: Filter = { ansicht: 'alle', person: '', status: 'aktiv', faellig: 'alle', suche: '' };

function filterAnwenden(liste: Aufgabe[], f: Filter, ichId: string): Aufgabe[] {
  const heute = heuteIso();
  const suche = f.suche.trim().toLowerCase();
  return liste.filter((a) => {
    if (f.ansicht === 'vorschlaege') return a.vorschlag;
    if (a.vorschlag) return false;
    if (f.ansicht === 'meine' && !a.zugewiesen.some((p) => p.id === ichId)) return false;
    if (f.person === '-' && a.zugewiesen.length) return false;
    if (f.person && f.person !== '-' && !a.zugewiesen.some((p) => p.id === f.person)) return false;
    if (f.status === 'aktiv' && a.status === 'erledigt') return false;
    if (f.status !== 'aktiv' && f.status !== 'alle' && a.status !== f.status) return false;
    if (f.faellig === 'ueberfaellig' && !istUeberfaellig(a, heute)) return false;
    if (f.faellig === 'heute' && a.faellig !== heute) return false;
    if (f.faellig === 'woche' && (!a.faellig || tageZwischen(heute, a.faellig) > 7 || a.status === 'erledigt')) return false;
    if (f.faellig === 'ohne' && a.faellig) return false;
    if (suche && !`${a.titel} ${a.beschreibung}`.toLowerCase().includes(suche)) return false;
    return true;
  });
}

export function Seite(props: ModulSeiteProps) {
  const { projekt, ich, darfSchreiben } = props;
  const auswahl = useParams()['*'] || null;
  const navigate = useNavigate();
  const basis = `/p/${projekt.id}/aufgaben`;
  const { liste, fehler, ersetzen, entfernen } = useAufgaben(projekt.id);
  const mitglieder = useMitglieder(projekt.id);
  const mindmap = useMindmap(projekt.id);
  const [filter, setFilter] = useState<Filter>(START);

  const sichtbar = useMemo(() => (liste ? filterAnwenden(liste, filter, ich.id) : []), [liste, filter, ich.id]);
  const vorschlaege = liste?.filter((a) => a.vorschlag).length ?? 0;
  const ueberfaellig = liste?.filter((a) => !a.vorschlag && istUeberfaellig(a)).length ?? 0;
  const gewaehlt = auswahl ? liste?.find((a) => a.id === auswahl) : undefined;

  const oeffnen = (id: string) => navigate(`${basis}/${id}`);
  const schliessen = () => navigate(basis);

  return (
    <div className={`seite auf-seite${auswahl ? ' auf-mit-detail' : ''}`}>
      <div className="auf-spalte-liste">
        <div className="auf-ansichten" role="tablist" aria-label="Ansicht">
          {(
            [
              ['alle', 'Alle'],
              ['meine', 'Meine'],
              ['vorschlaege', `Vorschläge${vorschlaege ? ` (${vorschlaege})` : ''}`],
              ['ueberall', 'Meine in allen Projekten'],
            ] as const
          ).map(([wert, text]) => (
            <button
              key={wert}
              type="button"
              role="tab"
              aria-selected={filter.ansicht === wert}
              className={`auf-ansicht${filter.ansicht === wert ? ' aktiv' : ''}${wert === 'vorschlaege' && vorschlaege ? ' auf-hat-vorschlaege' : ''}`}
              onClick={() => setFilter((f) => ({ ...f, ansicht: wert }))}
            >
              {text}
            </button>
          ))}
        </div>

        {filter.ansicht === 'ueberall' ? (
          <Ueberall ichId={ich.id} aktuellesProjekt={projekt.id} />
        ) : (
          <>
            {filter.ansicht !== 'vorschlaege' && (
              <FilterLeiste filter={filter} setFilter={setFilter} mitglieder={mitglieder} ueberfaellig={ueberfaellig} />
            )}
            {darfSchreiben && filter.ansicht !== 'vorschlaege' && (
              <Schnell
                projektId={projekt.id}
                fuerMich={filter.ansicht === 'meine' ? ich.id : filter.person && filter.person !== '-' ? filter.person : null}
                fertig={(a) => {
                  ersetzen(a);
                  oeffnen(a.id);
                }}
              />
            )}
            {vorschlaege > 0 && filter.ansicht !== 'vorschlaege' && (
              <button type="button" className="auf-hinweis-vorschlaege" onClick={() => setFilter((f) => ({ ...f, ansicht: 'vorschlaege' }))}>
                {Symbol.ki} {vorschlaege === 1 ? '1 Vorschlag wartet' : `${vorschlaege} Vorschläge warten`} auf Bestätigung – ansehen
              </button>
            )}
            <Meldung>{fehler}</Meldung>
            {!liste ? (
              <Laden />
            ) : sichtbar.length === 0 ? (
              <Leer titel={leerText(filter, liste.length)}>
                {filter.ansicht === 'alle' && liste.length === 0 && darfSchreiben && 'Oben einen Titel eintippen und Enter drücken.'}
              </Leer>
            ) : filter.ansicht === 'vorschlaege' ? (
              <div className="auf-vorschlaege">
                {sichtbar.map((a) => (
                  <VorschlagKarte
                    key={a.id}
                    a={a}
                    projektId={projekt.id}
                    darfSchreiben={darfSchreiben}
                    gewaehlt={a.id === auswahl}
                    oeffnen={() => oeffnen(a.id)}
                    geaendert={ersetzen}
                    weg={(id) => {
                      entfernen(id);
                      if (id === auswahl) schliessen();
                    }}
                  />
                ))}
              </div>
            ) : (
              <ul className="auf-liste">
                {sichtbar.map((a) => (
                  <AufgabenZeile
                    key={a.id}
                    a={a}
                    ichId={ich.id}
                    projektId={projekt.id}
                    darfSchreiben={darfSchreiben}
                    gewaehlt={a.id === auswahl}
                    oeffnen={() => oeffnen(a.id)}
                    geaendert={ersetzen}
                    mindmap={mindmap}
                  />
                ))}
              </ul>
            )}
            {liste && filter.ansicht !== 'vorschlaege' && <Zaehler gesamt={liste.filter((a) => !a.vorschlag)} sichtbar={sichtbar.length} />}
          </>
        )}
      </div>

      {auswahl && (
        <div className="auf-spalte-detail">
          {!liste ? (
            <Laden />
          ) : gewaehlt ? (
            <Detail
              key={gewaehlt.id}
              a={gewaehlt}
              {...props}
              mitglieder={mitglieder}
              mindmap={mindmap}
              schliessen={schliessen}
              geaendert={ersetzen}
              geloescht={(id) => {
                entfernen(id);
                schliessen();
              }}
            />
          ) : (
            <div className="karte">
              <Leer titel="Diese Aufgabe gibt es nicht (mehr).">
                <Knopf onClick={schliessen}>Zur Liste</Knopf>
              </Leer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function leerText(f: Filter, gesamt: number): string {
  if (f.ansicht === 'vorschlaege') return 'Keine offenen Vorschläge.';
  if (gesamt === 0) return 'Noch keine Aufgaben in diesem Projekt.';
  if (f.ansicht === 'meine') return 'Dir ist hier gerade nichts zugewiesen, das zum Filter passt.';
  return 'Keine Aufgabe passt zum Filter.';
}

function Zaehler({ gesamt, sichtbar }: { gesamt: Aufgabe[]; sichtbar: number }) {
  const erledigt = gesamt.filter((a) => a.status === 'erledigt').length;
  return (
    <p className="leise klein auf-zaehler">
      {sichtbar} angezeigt · {gesamt.length - erledigt} offen · {erledigt} erledigt
    </p>
  );
}

function FilterLeiste({
  filter,
  setFilter,
  mitglieder,
  ueberfaellig,
}: {
  filter: Filter;
  setFilter: (fn: (f: Filter) => Filter) => void;
  mitglieder: { id: string; name: string }[];
  ueberfaellig: number;
}) {
  const setze = <K extends keyof Filter>(k: K, v: Filter[K]) => setFilter((f) => ({ ...f, [k]: v }));
  const veraendert = filter.person || filter.status !== 'aktiv' || filter.faellig !== 'alle' || filter.suche;
  return (
    <div className="auf-filter">
      <Eingabe type="search" placeholder="Suchen …" value={filter.suche} onChange={(e) => setze('suche', e.target.value)} aria-label="Suchen" />
      {filter.ansicht !== 'meine' && (
        <Auswahl value={filter.person} onChange={(e) => setze('person', e.target.value)} aria-label="Person">
          <option value="">Alle Personen</option>
          <option value="-">Niemandem zugewiesen</option>
          {mitglieder.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </Auswahl>
      )}
      <Auswahl value={filter.status} onChange={(e) => setze('status', e.target.value)} aria-label="Status">
        <option value="aktiv">Nicht erledigt</option>
        <option value="alle">Alle Status</option>
        {AUFGABEN_STATUS.map((s) => (
          <option key={s} value={s}>
            {STATUS_NAMEN[s]}
          </option>
        ))}
      </Auswahl>
      <Auswahl value={filter.faellig} onChange={(e) => setze('faellig', e.target.value as Faelligkeit)} aria-label="Fälligkeit">
        <option value="alle">Jede Fälligkeit</option>
        <option value="ueberfaellig">Überfällig{ueberfaellig ? ` (${ueberfaellig})` : ''}</option>
        <option value="heute">Heute fällig</option>
        <option value="woche">In den nächsten 7 Tagen</option>
        <option value="ohne">Ohne Datum</option>
      </Auswahl>
      {veraendert && (
        <Knopf art="leise" klein onClick={() => setFilter((f) => ({ ...START, ansicht: f.ansicht }))}>
          Filter zurücksetzen
        </Knopf>
      )}
    </div>
  );
}

/** Schnell anlegen: Titel eintippen, Enter. */
function Schnell({ projektId, fuerMich, fertig }: { projektId: string; fuerMich: string | null; fertig(a: Aufgabe): void }) {
  const [titel, setTitel] = useState('');
  const aktion = useAktion();
  async function anlegen() {
    if (!titel.trim()) return;
    const a = await aktion.ausfuehren(() => api.post<Aufgabe>(pfad(projektId), { titel, zugewiesenAn: fuerMich ? [fuerMich] : [] }));
    if (a) {
      setTitel('');
      fertig(a);
    }
  }
  return (
    <form
      className="auf-schnell"
      onSubmit={(e) => {
        e.preventDefault();
        void anlegen();
      }}
    >
      <Eingabe
        placeholder="Neue Aufgabe – Titel eintippen und Enter"
        value={titel}
        onChange={(e) => setTitel(e.target.value)}
        maxLength={300}
        aria-label="Titel der neuen Aufgabe"
      />
      <Knopf art="haupt" type="submit" disabled={!titel.trim() || aktion.laeuft}>
        {Symbol.plus} Anlegen
      </Knopf>
      {aktion.fehler && <Meldung>{aktion.fehler}</Meldung>}
    </form>
  );
}

/** „Meine Aufgaben“ aus allen Projekten. */
function Ueberall({ ichId, aktuellesProjekt }: { ichId: string; aktuellesProjekt: string }) {
  const [erledigte, setErledigte] = useState(false);
  const { liste: daten, fehler } = useMeineAufgaben(erledigte, aktuellesProjekt);
  const navigate = useNavigate();

  const gruppen = useMemo(() => {
    const m = new Map<string, MeineAufgabe[]>();
    for (const a of daten ?? []) m.set(a.projektName, [...(m.get(a.projektName) ?? []), a]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b, 'de'));
  }, [daten]);

  return (
    <div className="stapel">
      <label className="schalter">
        <input type="checkbox" checked={erledigte} onChange={(e) => setErledigte(e.target.checked)} />
        <span>Erledigte auch zeigen</span>
      </label>
      <Meldung>{fehler}</Meldung>
      {!daten ? (
        <Laden />
      ) : daten.length === 0 ? (
        <Leer titel="Dir ist in keinem Projekt etwas zugewiesen." />
      ) : (
        gruppen.map(([name, liste]) => (
          <section key={name} className="stapel auf-gruppe">
            <h3>{name}</h3>
            <ul className="auf-liste">
              {liste.map((a) => (
                <AufgabenZeile
                  key={a.id}
                  a={a}
                  ichId={ichId}
                  projektId={a.projektId}
                  darfSchreiben={false}
                  gewaehlt={false}
                  oeffnen={() => navigate(`/p/${a.projektId}/aufgaben/${a.id}`)}
                  geaendert={() => undefined}
                  mindmap={{ verfuegbar: false, knoten: [] }}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
